import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiActivity, FiGrid, FiCpu, FiExternalLink } from 'react-icons/fi';
import { projects } from '../../data/projectData';
import ProjectModal from './ProjectModal';
import './Projects.css';

const gradientMap = {
  gainsLog: 'linear-gradient(135deg, #e74a32, #80475e)',
  codeNames: 'linear-gradient(135deg, #3b82f6, #6366f1)',
  aiNotesManager: 'linear-gradient(135deg, #10b981, #059669)',
};

const iconMap = {
  gainsLog: FiActivity,
  codeNames: FiGrid,
  aiNotesManager: FiCpu,
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.97 },
  show: {
    opacity: 1, y: 0, scale: 1,
    transition: { type: 'spring', stiffness: 120, damping: 20 }
  }
};

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const triggerRef = useRef(null);

  const openModal = (project) => {
    triggerRef.current = document.activeElement;
    setSelectedProject(project);
  };

  const closeModal = () => {
    setSelectedProject(null);
    requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
  };

  return (
    <div className="projects-container">
      <div className="projects-header">
        <h2 className="text-gradient">Featured Work</h2>
        <div className="divider"></div>
      </div>

      <motion.div
        className="projects-grid"
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
      >
        {projects.map((project) => {
          const IconComponent = iconMap[project.id];
          return (
            <motion.div
              key={project.id}
              className="project-card glass-panel"
              role="article"
              aria-label={`${project.title} project`}
              tabIndex={0}
              onClick={() => openModal(project)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(project); } }}
              variants={itemVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Banner -- per D-01: gradient bg + centered icon */}
              <div
                className="card-banner"
                style={{ background: gradientMap[project.id] }}
              >
                <div className="card-banner-inner">
                  <IconComponent size={48} aria-hidden="true" />
                </div>
              </div>

              {/* Card body */}
              <div className="card-body">
                <h3 className="card-title">{project.title}</h3>
                <p className="card-role">{project.role}</p>
                <p className="card-description">{project.description}</p>

                <div className="card-footer">
                  <div className="card-badges">
                    {project.techStack.slice(0, 3).map((tech) => (
                      <span key={tech} className="tech-badge">{tech}</span>
                    ))}
                  </div>

                  {/* Link action -- per D-08 and D-09 */}
                  {project.liveUrl ? (
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="skeuo-button small-btn"
                      aria-label={`View live demo of ${project.title}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      View Live <FiExternalLink size={14} />
                    </a>
                  ) : (
                    <span className="self-hosted-indicator">Self-hosted</span>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      <AnimatePresence>
        {selectedProject && (
          <ProjectModal
            key="project-modal"
            project={selectedProject}
            gradient={gradientMap[selectedProject.id]}
            IconComponent={iconMap[selectedProject.id]}
            onClose={closeModal}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Projects;
